import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { QRCodeSVG } from 'qrcode.react';
import { useI18n } from '../../i18n/I18nContext';
import api from '../../utils/api';

function CouponPage() {
  const { token } = useParams();
  const { t, locale, setLocale, availableLocales } = useI18n();

  const [coupon, setCoupon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    async function loadCoupon() {
      try {
        const data = await api.get(`/guest/coupon/${token}?locale=${locale}`);
        setCoupon(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    }
    loadCoupon();
  }, [token, locale]);

  if (loading) {
    return (
      <div className="container" style={{ paddingTop: '40px' }}>
        <div className="flex justify-center">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container" style={{ paddingTop: '40px', textAlign: 'center' }}>
        <h2>{t('coupon.notFound')}</h2>
        <p className="text-secondary mt-2">{error}</p>
      </div>
    );
  }

  const isExpired = coupon.status === 'expired';
  const isRedeemed = coupon.status === 'redeemed';
  const isValid = coupon.status === 'unused';

  const redemptionUrl = `${window.location.origin}/r/${coupon.token}`;

  return (
    <div style={{ minHeight: '100vh', background: 'var(--background)' }}>
      {/* Language switcher */}
      <div style={{ position: 'absolute', top: '12px', right: '12px' }}>
        <select
          value={locale}
          onChange={(e) => setLocale(e.target.value)}
          style={{
            padding: '6px 10px',
            borderRadius: '6px',
            border: '1px solid var(--border)',
            fontSize: '14px'
          }}
        >
          {availableLocales.map(l => (
            <option key={l} value={l}>{l.toUpperCase()}</option>
          ))}
        </select>
      </div>

      <div className="container" style={{ paddingTop: '60px', paddingBottom: '32px' }}>
        <div className="card" style={{ textAlign: 'center' }}>
          {/* Status badge */}
          {isExpired && (
            <div style={{ background: '#fef2f2', color: '#dc2626', padding: '8px', borderRadius: '6px', marginBottom: '16px' }}>
              {t('coupon.expired')}
            </div>
          )}
          {isRedeemed && (
            <div style={{ background: '#f0fdf4', color: '#16a34a', padding: '8px', borderRadius: '6px', marginBottom: '16px' }}>
              {t('coupon.redeemed')}
            </div>
          )}

          {/* Vendor & Offer info */}
          <h2 style={{ fontSize: '18px', marginBottom: '4px' }}>{coupon.vendor_name}</h2>
          <h1 style={{ fontSize: '24px', marginBottom: '8px' }}>{coupon.title}</h1>
          <span className="badge badge-category">{t(`categories.${coupon.category_slug}`, { fallback: coupon.category_name })}</span>

          {coupon.short_description && (
            <p className="text-secondary mt-3">{coupon.short_description}</p>
          )}

          {/* QR Code */}
          {isValid && (
            <div style={{ marginTop: '24px', marginBottom: '16px' }}>
              <p className="text-secondary text-sm mb-2">{t('coupon.showToVendor')}</p>
              <div style={{ background: 'white', padding: '16px', display: 'inline-block', borderRadius: '8px' }}>
                <QRCodeSVG value={redemptionUrl} size={200} />
              </div>
            </div>
          )}

          {/* Code */}
          <p className="text-secondary text-sm">{t('coupon.orUseCode')}</p>
          <div style={{
            fontSize: '32px',
            fontWeight: 'bold',
            letterSpacing: '4px',
            fontFamily: 'monospace',
            padding: '16px',
            background: 'var(--background)',
            borderRadius: '8px',
            margin: '8px 0 16px'
          }}>
            {coupon.token}
          </div>

          {/* Expiry */}
          {coupon.expires_at && isValid && (
            <p className="text-secondary text-sm">
              {t('coupon.validUntil', { date: new Date(coupon.expires_at).toLocaleDateString() })}
            </p>
          )}

          {/* Fine print */}
          {coupon.fine_print && (
            <p className="text-secondary text-xs mt-3" style={{ fontStyle: 'italic' }}>
              {coupon.fine_print}
            </p>
          )}
        </div>

        {/* Travel info */}
        {(coupon.walk_time_min || coupon.drive_time_min) && (
          <div className="card">
            <h3 style={{ fontSize: '14px', marginBottom: '12px' }}>{t('coupon.travelInfo')}</h3>
            <p className="text-secondary text-sm">{t('coupon.fromProperty')}</p>
            <div className="flex gap-3 mt-2">
              {coupon.walk_time_min && (
                <div>
                  <span style={{ fontSize: '20px' }}>🚶</span>
                  <span className="text-sm ml-1">{t('coupon.walkTime', { time: coupon.walk_time_min })}</span>
                </div>
              )}
              {coupon.drive_time_min && (
                <div>
                  <span style={{ fontSize: '20px' }}>🚗</span>
                  <span className="text-sm ml-1">{t('coupon.driveTime', { time: coupon.drive_time_min })}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Contact actions */}
        <div className="card">
          <h3 style={{ fontSize: '14px', marginBottom: '12px' }}>{t('coupon.contactVendor')}</h3>
          <div className="flex flex-col gap-2">
            {coupon.website_url && (
              <a href={coupon.website_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-block">
                {t('coupon.openWebsite')}
              </a>
            )}
            {coupon.whatsapp_number && (
              <a href={`https://wa.me/${coupon.whatsapp_number.replace(/\D/g, '')}?text=${encodeURIComponent(`Hi! I have coupon ${coupon.token} for "${coupon.title}". I'd like to use my discount.`)}`} className="btn btn-secondary btn-block">
                {t('coupon.chatWhatsApp')}
              </a>
            )}
            {coupon.line_url && (
              <a href={coupon.line_url.includes('line.me/ti/p/')
                ? coupon.line_url.replace('line.me/ti/p/', 'line.me/R/oaMessage/') + `?text=${encodeURIComponent(`Hi! I have coupon ${coupon.token} for "${coupon.title}". I'd like to use my discount.`)}`
                : coupon.line_url
              } target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-block">
                {t('coupon.chatLine')}
              </a>
            )}
            {coupon.phone_number && (
              <a href={`tel:${coupon.phone_number}`} className="btn btn-secondary btn-block">
                {t('coupon.callVendor')}: {coupon.phone_number}
              </a>
            )}
            {coupon.google_maps_url && (
              <a href={coupon.google_maps_url} target="_blank" rel="noopener noreferrer" className="btn btn-secondary btn-block">
                {t('coupon.openMaps')}
              </a>
            )}
          </div>
        </div>

        {/* Back link */}
        <Link
          to={`/u/${coupon.unit_code}`}
          className="btn btn-secondary btn-block"
          style={{ marginTop: '16px' }}
        >
          {t('coupon.backToOffers')}
        </Link>
      </div>
    </div>
  );
}

export default CouponPage;
